import { Component } from "vue-property-decorator";
import { API_ENDPOINT } from "@/constants";
import { Unit } from "@/components/Agencies/ReportBuilder/reportUnit";
import { ReportTemplate } from "@/models";
import Base from "./Base";

const HTTP_REQUEST_ACTION = "http/request";

export enum ReportTemplatesTags {
  INDEX = "get_report_templates",
  CREATE = "create_report_templates",
  DUPLICATE = "duplicate_report_templates",
  SHOW = "show_report_templates",
  UPDATE = "update_report_templates",
  DESTROY = "destroy_report_templates",
  BULK_DESTROY = "bulk_destroy_report_templates",
}

export enum OutputType {
  FULL = "full",
  RAW = "raw",
}

export enum TemplateKind {
  COMPETITOR = "competitor",
  CALENDAR = "calendar",
  CAMPAIGN = "campaign",
}

type BaseReportTemplateParameters = {
  name: string;
  kind: TemplateKind;
};

type KindTemplateAttributes =
  | {
      competitor_attributes: CompetitorReportTemplateParameters;
      calendar_attributes?: never;
      campaign_attributes?: never;
    }
  | {
      competitor_attributes?: never;
      calendar_attributes: CalendarReportTemplateParameters; // TODO: map it
      campaign_attributes?: never;
    }
  | {
      competitor_attributes?: never;
      calendar_attributes?: never;
      campaign_attributes: CampaignReportTemplateParameters; // TODO: map it
    };

export type ReportTemplateParameters = BaseReportTemplateParameters &
  KindTemplateAttributes;

export type DuplicateReportTemplateParameters = Pick<
  BaseReportTemplateParameters,
  "name"
>;

export type FiltersListPermittedParams = Array<"all" | number> | null;
export type IndexTemplateParameters = {
  kind?: TemplateKind;
};

// TODO: implement this in future if they will be created
export type CampaignReportTemplateParameters = Record<string, never>;
export type CalendarReportTemplateParameters = Record<string, never>;

export type CompetitorReportTemplateParameters = {
  // step 2
  competitors?: FiltersListPermittedParams; // broadcasters
  agencies?: FiltersListPermittedParams; // agencies

  // step 3
  companies?: FiltersListPermittedParams;
  brands?: FiltersListPermittedParams;
  products?: FiltersListPermittedParams;
  sectors?: FiltersListPermittedParams;
  categories?: FiltersListPermittedParams;
  classes?: FiltersListPermittedParams;

  apply_client_database: boolean;
  attach_sector_category_class: boolean;

  // step 4
  raw: boolean;
  type: Unit;
  format_detail: boolean;
  temporal_detail: boolean;
  week_type: "eu";
  quota: boolean;
  inclusion: boolean;
  show_special_initiatives?: boolean;
  granularity?: string | null;
  auditel_target?: string | null;
  show_vod_devices: boolean;
};

const KIND_ATTRIBUTE_KEYS = [
  "competitor_attributes",
  "calendar_attributes",
  "campaign_attributes",
];

@Component
export default class ReportTemplateMixin extends Base {
  indexReportTemplates(
    profileSlug: string,
    params?: IndexTemplateParameters
  ): Promise<ReportTemplate[]> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates.json`;
    const queryParams = params?.kind ? { kind: params.kind } : {};

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.INDEX,
      url,
      method: "GET",
      params: queryParams,
    });
  }

  createReportTemplate(
    profileSlug: string,
    params: ReportTemplateParameters
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.CREATE,
      url,
      method: "POST",
      data: { ...params },
    });
  }

  duplicateReportTemplate(
    profileSlug: string,
    templateId: string,
    params: DuplicateReportTemplateParameters
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates/${templateId}/duplicate.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.DUPLICATE,
      url,
      method: "POST",
      data: { ...params },
    });
  }

  showReportTemplate(
    profileSlug: string,
    templateId: string
  ): Promise<ReportTemplate> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates/${templateId}.json`;
    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.SHOW,
      url,
      method: "GET",
    });
  }

  updateReportTemplate(
    profileSlug: string,
    templateId: string,
    params: ReportTemplateParameters
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates/${templateId}.json`;

    // Root-level nulls are stripped from the payload, except the kind
    // attributes (competitor/calendar/campaign): sending those as null tells
    // the backend to null the corresponding table fields. Nulls nested inside
    // the kind attributes are valid values and pass through untouched.
    const data = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null || KIND_ATTRIBUTE_KEYS.includes(key)
      )
    );

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.UPDATE,
      url,
      method: "PATCH",
      data,
    });
  }

  destroyReportTemplate(
    profileSlug: string,
    templateId: string,
    force: boolean
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates/${templateId}.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.DESTROY,
      url,
      method: "DELETE",
      data: { force },
    });
  }

  bulkDestroyReportTemplates(
    profileSlug: string,
    templateIds: string[],
    force: boolean
  ): Promise<unknown> {
    const url = `${API_ENDPOINT}/${profileSlug}/report_templates/bulk_destroy.json`;

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportTemplatesTags.BULK_DESTROY,
      url,
      method: "DELETE",
      data: { ids: templateIds, force },
    });
  }
}
